/**
 * PM-5 — TanStack Query mutations for the projects/v1 `project-import`
 * analyze surface (dry-run; writes nothing). Mirrors
 * `features/ai-findings-import/importQueries.ts` (`useAnalyzeImportMutation`),
 * but the projects feature talks to its own domain via raw `fetch` +
 * `getProjectsBaseUrl()` (the `projectsFetch.ts` pattern) rather than the
 * lyse-pinned `useApi()` axios instance.
 *
 * Two encodings of `POST /imports/analyze`:
 *  - First pass — `multipart/form-data` with the uploaded file (+ optional
 *    chosen sheet/header). The BE parses (PM-D6) and returns the parsed
 *    workbook so sheet/header/mapping/edit happen client-side.
 *  - Subsequent passes — `application/json` (`RevalidateImportBody`): the
 *    edited mapping + toggled rows + edited cell values re-validate WITHOUT
 *    a re-upload — the edited grid travels in the body (PM-D6).
 *
 * Commit (`POST /imports/commit`) + status + cancel are PM-6 — NOT here.
 *
 * SYNC: projects/v1@2.7.0 (project-import).
 */
import { useMutation } from "@tanstack/react-query";
import { getProjectsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import type { AnalyzeImportReport, RevalidateImportBody } from "./types.js";

interface ImportFetchError extends Error {
  status: number;
  body: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

async function readError(
  path: string,
  res: Response,
): Promise<ImportFetchError> {
  let parsedBody: unknown = null;
  try {
    parsedBody = await res.json();
  } catch {
    // non-JSON error body — status still surfaces
  }
  const err = new Error(
    `import analyze failed: ${path} → ${String(res.status)}`,
  ) as ImportFetchError;
  err.status = res.status;
  err.body = parsedBody;
  return err;
}

/** First-pass multipart upload payload. */
export interface AnalyzeUploadVars {
  file: File;
  /** Optional pre-chosen sheet (when re-deriving a different sheet). */
  sheetName?: string;
  /** Optional pre-chosen 0-based header row. */
  headerRow?: number;
}

/**
 * POST /imports/analyze (multipart) — first pass. Uploads the workbook and
 * returns the parsed sheets + suggested sheet/header + fuzzy mapping +
 * per-row validation. No writes.
 */
export function useAnalyzeUploadMutation() {
  return useMutation<AnalyzeImportReport, ImportFetchError, AnalyzeUploadVars>({
    mutationFn: async ({ file, sheetName, headerRow }) => {
      const form = new FormData();
      form.append("file", file);
      if (sheetName !== undefined) form.append("sheetName", sheetName);
      if (headerRow !== undefined) form.append("headerRow", String(headerRow));
      const headers = authHeaders();
      // Do NOT set Content-Type — the browser sets the multipart boundary.
      const path = "/imports/analyze";
      const res = await fetch(`${getProjectsBaseUrl()}${path}`, {
        method: "POST",
        headers,
        body: form,
      });
      if (!res.ok) throw await readError(path, res);
      return (await res.json()) as AnalyzeImportReport;
    },
  });
}

/**
 * POST /imports/analyze (JSON) — subsequent re-validate passes. The edited
 * mapping + toggled rows + edited cell values travel in the body (PM-D6, no
 * re-upload). Returns a fresh report.
 */
export function useRevalidateMutation() {
  return useMutation<
    AnalyzeImportReport,
    ImportFetchError,
    RevalidateImportBody
  >({
    mutationFn: async (body) => {
      const headers = authHeaders();
      headers.set("Content-Type", "application/json");
      const path = "/imports/analyze";
      const res = await fetch(`${getProjectsBaseUrl()}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await readError(path, res);
      return (await res.json()) as AnalyzeImportReport;
    },
  });
}
